/**
 * Business Unit Matrix Dashboard Route
 *
 * Executive-level BU profitability dashboard with:
 * - Hero KPIs (Receita, Custos, Margem, PITs, Produtos, Notas)
 * - Period comparison (vs Previous Period / vs Same Period Last Year)
 * - Interactive charts (Evolution by BU, Composition)
 * - BU-aggregated table with drill-down links (Digital vs Promo)
 *
 * Business Units are defined by product codes:
 * - Digital: 3.01.0005, 3.01.0022, 3.02.0002
 * - Promo: All other products
 *
 * Route: /_auth/financial/bu-matrix/
 */

import { trpc } from '@/api/trpc';
import {
  DREControlsPanel,
  calculateComparisonRange,
  calculatePeriodDates,
  type ComparisonMode,
  type DREGranularity,
  type PeriodDates,
} from '@/app/financial/dre/components';
import { getMarginCellStyles } from '@/app/financial/shared/constants';
import {
  KPICard,
  BottomActionSheet,
  FloatingActionButton,
  type ActionItem,
} from '@/app/financial/dre/drill-down/components';
import { TableSparkline } from '@/app/financial/shared/components';
import { useFilterSync } from '@/app/financial/shared/hooks/useFilterSync';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from '@tanstack/react-table';
import { useCallback, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { SensitiveValue } from '@/components/privacy/SensitiveText';

// ============================================================================
// CONSTANTS
// ============================================================================

// Valid granularity values for URL validation
const VALID_GRANULARITIES: DREGranularity[] = ['accumulated', 'yearly', 'quarterly', 'monthly'];

// BU Colors
const BU_COLORS = {
  Digital: '#00BCD4', // Cyan for Digital
  Promo: '#FF9800',   // Orange for Promo
};

// ============================================================================
// TYPES
// ============================================================================

// BU aggregation from backend (with added percentOfTotal for frontend)
interface BUAggregation {
  businessUnit: string;
  receita: number;
  custos: number;
  margem: number;
  margemPercent: number;
  notasCount: number;
  pitsCount: number;
  produtosCount: number;
  percentOfTotal: number;
}

// Chart data point for BU comparison
interface BUChartDataPoint {
  period: string;
  label: string;
  digital: { receita: number; custos: number; margem: number };
  promo: { receita: number; custos: number; margem: number };
}

// KPI definitions
interface KPIDefinition {
  key: string;
  label: string;
  icon: string;
  color: string;
  getValue: (totals: Record<string, number>) => number;
  format?: 'currency' | 'number' | 'percent';
  getSubtitle?: (value: number, totals: Record<string, number>) => string;
}

const KPI_DEFINITIONS: KPIDefinition[] = [
  {
    key: 'receita',
    label: 'Receita',
    icon: '',
    color: '#D2FA52',
    getValue: (t) => t['receita'] || 0,
  },
  {
    key: 'custos',
    label: 'Custos',
    icon: '',
    color: '#ff6b6b',
    getValue: (t) => t['custos'] || 0,
  },
  {
    key: 'margem',
    label: 'Margem',
    icon: '',
    color: '#D2FA52',
    getValue: (t) => t['margem'] || 0,
    getSubtitle: (value, totals) => {
      const receita = totals['receita'] || 0;
      return receita > 0 ? `${((value / receita) * 100).toFixed(1)}%` : '';
    },
  },
  {
    key: 'pits',
    label: 'PITs',
    icon: '',
    color: '#9c27b0',
    getValue: (t) => t['pits'] || 0,
    format: 'number',
  },
  {
    key: 'produtos',
    label: 'Produtos',
    icon: '',
    color: '#ff9800',
    getValue: (t) => t['produtos'] || 0,
    format: 'number',
  },
  {
    key: 'notas',
    label: 'Notas',
    icon: '',
    color: '#607d8b',
    getValue: (t) => t['notas'] || 0,
    format: 'number',
  },
];

// ============================================================================
// ROUTE DEFINITION
// ============================================================================

export const Route = createFileRoute('/_auth/financial/bu-matrix/')({
  component: BUMatrixDashboard,
});

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

function formatCurrency(value: number | null): string {
  if (value === null || value === undefined || isNaN(value)) return '-';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatCompactCurrency(value: number): string {
  if (value === null || value === undefined || isNaN(value)) return '-';
  const absValue = Math.abs(value);
  if (absValue >= 1000000) {
    return `R$ ${(value / 1000000).toFixed(1)}M`;
  }
  if (absValue >= 1000) {
    return `R$ ${(value / 1000).toFixed(0)}K`;
  }
  return formatCurrency(value);
}

function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`;
}

function getDefaultDateRange(): PeriodDates {
  return calculatePeriodDates('ytd');
}

function getQuarter(month: number): number {
  return Math.ceil(month / 3);
}

// Aggregate monthly chart data by granularity for BU
function aggregateBUChartData(
  monthlyData: BUChartDataPoint[],
  granularity: DREGranularity
): BUChartDataPoint[] {
  if (granularity === 'monthly') {
    return monthlyData;
  }

  if (granularity === 'accumulated') {
    const totals = monthlyData.reduce(
      (acc, m) => ({
        digital: {
          receita: acc.digital.receita + m.digital.receita,
          custos: acc.digital.custos + m.digital.custos,
          margem: acc.digital.margem + m.digital.margem,
        },
        promo: {
          receita: acc.promo.receita + m.promo.receita,
          custos: acc.promo.custos + m.promo.custos,
          margem: acc.promo.margem + m.promo.margem,
        },
      }),
      {
        digital: { receita: 0, custos: 0, margem: 0 },
        promo: { receita: 0, custos: 0, margem: 0 },
      }
    );
    return [{
      period: 'total',
      label: 'Acumulado',
      ...totals,
    }];
  }

  if (granularity === 'yearly') {
    const byYear: Record<string, { digital: { receita: number; custos: number; margem: number }; promo: { receita: number; custos: number; margem: number } }> = {};
    monthlyData.forEach(m => {
      const year = m.period.split('-')[0];
      if (!byYear[year]) byYear[year] = {
        digital: { receita: 0, custos: 0, margem: 0 },
        promo: { receita: 0, custos: 0, margem: 0 },
      };
      byYear[year].digital.receita += m.digital.receita;
      byYear[year].digital.custos += m.digital.custos;
      byYear[year].digital.margem += m.digital.margem;
      byYear[year].promo.receita += m.promo.receita;
      byYear[year].promo.custos += m.promo.custos;
      byYear[year].promo.margem += m.promo.margem;
    });
    return Object.entries(byYear)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([year, data]) => ({
        period: year,
        label: year,
        ...data,
      }));
  }

  if (granularity === 'quarterly') {
    const byQuarter: Record<string, { digital: { receita: number; custos: number; margem: number }; promo: { receita: number; custos: number; margem: number } }> = {};
    monthlyData.forEach(m => {
      const [year, monthStr] = m.period.split('-');
      const quarter = getQuarter(parseInt(monthStr, 10));
      const key = `${year}-Q${quarter}`;
      if (!byQuarter[key]) byQuarter[key] = {
        digital: { receita: 0, custos: 0, margem: 0 },
        promo: { receita: 0, custos: 0, margem: 0 },
      };
      byQuarter[key].digital.receita += m.digital.receita;
      byQuarter[key].digital.custos += m.digital.custos;
      byQuarter[key].digital.margem += m.digital.margem;
      byQuarter[key].promo.receita += m.promo.receita;
      byQuarter[key].promo.custos += m.promo.custos;
      byQuarter[key].promo.margem += m.promo.margem;
    });
    return Object.entries(byQuarter)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, data]) => {
        const [year, q] = key.split('-');
        return {
          period: key,
          label: `${q}/${year}`,
          ...data,
        };
      });
  }

  return monthlyData;
}

// ============================================================================
// LOADING SKELETON (Monutrack Dark Theme)
// ============================================================================

function LoadingSkeleton(): JSX.Element {
  return (
    <div className="p-8 min-h-screen">
      <div className="mb-6">
        <div className="w-[250px] h-9 animate-pulse bg-brand-dark-light rounded-lg mb-2" />
        <div className="w-[400px] h-5 animate-pulse bg-brand-dark-light rounded" />
      </div>
      <div className="flex gap-4 mb-6">
        {[1, 2, 3, 4, 5, 6].map(i => (
          <div key={i} className="flex-1 h-[100px] animate-pulse bg-brand-dark-light rounded-lg" />
        ))}
      </div>
      <div className="w-full h-[400px] animate-pulse bg-brand-dark-light rounded-lg" />
    </div>
  );
}

// ============================================================================
// BU CHART COMPONENT - Stacked Bar Chart for comparing Digital vs Promo (using Recharts)
// ============================================================================

interface BUChartProps {
  data: BUChartDataPoint[];
  selectedMetric: 'receita' | 'custos' | 'margem';
  height?: number;
}

function BUChart({ data, selectedMetric, height = 280 }: BUChartProps): JSX.Element {
  if (!data.length) {
    return <div className="text-brand-gray text-center p-8">Sem dados</div>;
  }

  // Transform data for Recharts stacked bar chart
  const chartData = data.map(d => ({
    label: d.label,
    Digital: d.digital[selectedMetric],
    Promo: d.promo[selectedMetric],
  }));

  const metricLabels = {
    receita: 'Receita',
    custos: 'Custos',
    margem: 'Margem',
  };

  return (
    <div className="h-72">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
          <XAxis
            dataKey="label"
            stroke="#9CA3AF"
            fontSize={11}
            angle={-45}
            textAnchor="end"
            height={80}
          />
          <YAxis
            stroke="#9CA3AF"
            fontSize={12}
            tickFormatter={(value) => formatCompactCurrency(value)}
          />
          <Tooltip
            content={({ active, payload, label }) => {
              if (active && payload && payload.length) {
                return (
                  <div style={{
                    backgroundColor: '#1F2937',
                    border: '1px solid #374151',
                    borderRadius: '8px',
                    color: 'white',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                    padding: '10px'
                  }}>
                    <p style={{ marginBottom: '5px' }}>{`${metricLabels[selectedMetric]} - ${label}`}</p>
                    {payload.map((entry: any, index: number) => (
                      <p key={index} style={{ color: entry.color, marginBottom: '3px' }}>
                        {entry.name}: <SensitiveValue mask="R$ ••••••">
                          {formatCurrency(entry.value as number)}
                        </SensitiveValue>
                      </p>
                    ))}
                  </div>
                );
              }
              return null;
            }}
          />
          <Legend
            wrapperStyle={{ paddingTop: '20px' }}
          />
          <Bar
            dataKey="Digital"
            stackId="a"
            fill={BU_COLORS.Digital}
            name="Digital"
            radius={[0, 0, 0, 0]}
          />
          <Bar
            dataKey="Promo"
            stackId="a"
            fill={BU_COLORS.Promo}
            name="Promo"
            radius={[2, 2, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

function BUMatrixDashboard() {
  const navigate = useNavigate();
  const defaultDates = getDefaultDateRange();

  // Use filter sync for URL-backed state management
  const { state: filterState, setFilter, setFilters } = useFilterSync({
    defaults: {
      startDate: defaultDates.startDate,
      endDate: defaultDates.endDate,
      granularity: 'accumulated', // Default to YTD (accumulated 1 year) as per US-005
      comparisonMode: 'none',
      chartType: 'bar',
      selectedDimensions: [],
    },
  });

  // Extract filter values for convenience
  const { startDate, endDate, granularity, comparisonMode } = filterState;

  // Local state for non-URL-backed state
  const [sorting, setSorting] = useState<SortingState>([{ id: 'receita', desc: true }]);
  const [selectedChartMetric, setSelectedChartMetric] = useState<'receita' | 'custos' | 'margem'>('receita');
  const [isActionSheetOpen, setIsActionSheetOpen] = useState(false);

  // Calculate comparison date range
  const comparisonRange = useMemo(
    () => calculateComparisonRange(startDate, endDate, comparisonMode),
    [startDate, endDate, comparisonMode]
  );

  // Fetch BU matrix data
  const {
    data: matrixResult,
    isLoading: isLoadingMatrix,
    error: matrixError,
    refetch: refetchMatrix,
  } = trpc.dre.getBUMatrix.useQuery(
    {
      dataInicial: new Date(startDate),
      dataFinal: new Date(endDate),
    },
    {
      enabled: !!startDate && !!endDate,
      retry: false,
      staleTime: 5 * 60 * 1000,
    }
  );

  // Fetch comparison BU matrix data
  const {
    data: comparisonMatrixResult,
  } = trpc.dre.getBUMatrix.useQuery(
    {
      dataInicial: comparisonRange ? new Date(comparisonRange.startDate) : new Date(),
      dataFinal: comparisonRange ? new Date(comparisonRange.endDate) : new Date(),
    },
    {
      enabled: !!comparisonRange,
      retry: false,
      staleTime: 5 * 60 * 1000,
    }
  );

  const isLoading = isLoadingMatrix;
  const error = matrixError;

  const refetch = useCallback(() => {
    void refetchMatrix();
  }, [refetchMatrix]);

  // Raw monthly data from backend
  const rawMonthlyData: BUChartDataPoint[] = useMemo(() => {
    if (!matrixResult?.monthly) return [];
    return matrixResult.monthly.map(m => ({
      period: m.period ?? '',
      label: m.label ?? '',
      digital: m.digital ?? { receita: 0, custos: 0, margem: 0 },
      promo: m.promo ?? { receita: 0, custos: 0, margem: 0 },
    }));
  }, [matrixResult]);

  // Chart data aggregated by granularity
  const chartData: BUChartDataPoint[] = useMemo(() => {
    return aggregateBUChartData(rawMonthlyData, granularity);
  }, [rawMonthlyData, granularity]);

  // BU aggregations from backend (add percentOfTotal)
  const buAggregations = useMemo((): BUAggregation[] => {
    if (!matrixResult?.businessUnits) return [];
    const totalReceita = matrixResult.totals?.receita ?? 0;
    return matrixResult.businessUnits.map(bu => ({
      businessUnit: bu.businessUnit ?? '',
      receita: bu.receita ?? 0,
      custos: bu.custos ?? 0,
      margem: bu.margem ?? 0,
      margemPercent: bu.margemPercent ?? 0,
      notasCount: bu.notasCount ?? 0,
      pitsCount: bu.pitsCount ?? 0,
      produtosCount: bu.produtosCount ?? 0,
      percentOfTotal: totalReceita > 0 ? ((bu.receita ?? 0) / totalReceita) * 100 : 0,
    }));
  }, [matrixResult]);

  // Comparison aggregations
  const comparisonAggregations = useMemo((): Map<string, BUAggregation> => {
    if (!comparisonRange || !comparisonMatrixResult?.businessUnits) return new Map();
    const totalReceita = comparisonMatrixResult.totals?.receita ?? 0;
    const map = new Map<string, BUAggregation>();
    comparisonMatrixResult.businessUnits.forEach(bu => {
      const businessUnit = bu.businessUnit ?? '';
      map.set(businessUnit, {
        businessUnit,
        receita: bu.receita ?? 0,
        custos: bu.custos ?? 0,
        margem: bu.margem ?? 0,
        margemPercent: bu.margemPercent ?? 0,
        notasCount: bu.notasCount ?? 0,
        pitsCount: bu.pitsCount ?? 0,
        produtosCount: bu.produtosCount ?? 0,
        percentOfTotal: totalReceita > 0 ? ((bu.receita ?? 0) / totalReceita) * 100 : 0,
      });
    });
    return map;
  }, [comparisonRange, comparisonMatrixResult]);

  // Totals from backend
  const totals = useMemo((): Record<string, number> => {
    if (!matrixResult?.totals) {
      return { receita: 0, custos: 0, margem: 0, pits: 0, produtos: 0, notas: 0 };
    }
    return {
      receita: matrixResult.totals.receita ?? 0,
      custos: matrixResult.totals.custos ?? 0,
      margem: matrixResult.totals.margem ?? 0,
      pits: matrixResult.totals.pitsCount ?? 0,
      produtos: matrixResult.totals.produtosCount ?? 0,
      notas: matrixResult.totals.notasCount ?? 0,
    };
  }, [matrixResult]);

  // Comparison totals
  const comparisonTotals = useMemo((): Record<string, number> | null => {
    if (!comparisonRange || !comparisonMatrixResult?.totals) return null;
    return {
      receita: comparisonMatrixResult.totals.receita ?? 0,
      custos: comparisonMatrixResult.totals.custos ?? 0,
      margem: comparisonMatrixResult.totals.margem ?? 0,
      pits: comparisonMatrixResult.totals.pitsCount ?? 0,
      produtos: comparisonMatrixResult.totals.produtosCount ?? 0,
      notas: comparisonMatrixResult.totals.notasCount ?? 0,
    };
  }, [comparisonRange, comparisonMatrixResult]);

  // Calculate delta
  const calcDelta = (current: number, comparison: number | undefined): number | null => {
    if (comparison === undefined || comparison === 0) return null;
    return ((current - comparison) / Math.abs(comparison)) * 100;
  };

  // Handle drill-down navigation
  const handleDrillDown = useCallback((businessUnit: string) => {
    void navigate({
      to: '/financial/bu-matrix/$buId',
      params: { buId: businessUnit },
      search: { startDate, endDate, granularity },
    });
  }, [navigate, startDate, endDate, granularity]);

  // Handle CSV export
  const handleExportCSV = useCallback(() => {
    const headers = ['Business Unit', 'Notas', 'PITs', 'Produtos', 'Receita', 'Custos', 'Margem', 'Margem %', '% Total'];
    const rows = buAggregations.map(bu => [
      bu.businessUnit,
      bu.notasCount,
      bu.pitsCount,
      bu.produtosCount,
      bu.receita.toFixed(2),
      bu.custos.toFixed(2),
      bu.margem.toFixed(2),
      bu.margemPercent.toFixed(2),
      bu.percentOfTotal.toFixed(2),
    ]);
    const csv = [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `matriz-bu-${startDate}-${endDate}.csv`;
    link.click();
  }, [buAggregations, startDate, endDate]);

  // Mobile action handlers
  const handleShare = useCallback(async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'Matriz de BU - Monutrack',
          url: window.location.href,
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        toast.success('Link copiado para a area de transferencia');
      }
    } catch (error) {
      if ((error as Error).name !== 'AbortError') {
        toast.error('Erro ao compartilhar');
      }
    }
  }, []);

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success('Link copiado para a area de transferencia');
    } catch {
      toast.error('Erro ao copiar link');
    }
  }, []);

  const handleGoBack = useCallback(() => {
    void navigate({ to: '/financial' });
  }, [navigate]);

  // Mobile action items
  const mobileActions: ActionItem[] = useMemo(() => [
    {
      label: 'Exportar CSV',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
      ),
      onClick: handleExportCSV,
    },
    {
      label: 'Compartilhar',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="18" cy="5" r="3" />
          <circle cx="6" cy="12" r="3" />
          <circle cx="18" cy="19" r="3" />
          <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
          <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
        </svg>
      ),
      onClick: handleShare,
    },
    {
      label: 'Copiar Link',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
        </svg>
      ),
      onClick: handleCopyLink,
    },
    {
      label: 'Voltar',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="19" y1="12" x2="5" y2="12" />
          <polyline points="12 19 5 12 12 5" />
        </svg>
      ),
      onClick: handleGoBack,
    },
  ], [handleExportCSV, handleShare, handleCopyLink, handleGoBack]);

  // Column helper
  const columnHelper = createColumnHelper<BUAggregation>();

  // Build columns
  const columns = useMemo(() => [
    columnHelper.accessor('businessUnit', {
      header: 'Business Unit',
      cell: (info) => {
        const bu = info.getValue();
        return (
          <div
            onClick={() => handleDrillDown(bu)}
            className="cursor-pointer font-semibold text-base flex items-center gap-2"
            style={{ color: BU_COLORS[bu as keyof typeof BU_COLORS] || '#D2FA52' }}
          >
            <span
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: BU_COLORS[bu as keyof typeof BU_COLORS] || '#D2FA52' }}
            />
            {bu}
          </div>
        );
      },
    }),
    columnHelper.accessor('notasCount', {
      header: () => <div className="text-right">Notas</div>,
      cell: (info) => <div className="text-right text-brand-gray-light">{info.getValue()}</div>,
    }),
    columnHelper.accessor('pitsCount', {
      header: () => <div className="text-right">PITs</div>,
      cell: (info) => <div className="text-right text-brand-gray-light">{info.getValue()}</div>,
    }),
    columnHelper.accessor('produtosCount', {
      header: () => <div className="text-right">Produtos</div>,
      cell: (info) => <div className="text-right text-brand-gray-light">{info.getValue()}</div>,
    }),
    columnHelper.accessor('receita', {
      header: () => <div className="text-right">Receita</div>,
      cell: (info) => {
        const row = info.row.original;
        const compRow = comparisonAggregations.get(row.businessUnit);
        const delta = calcDelta(row.receita, compRow?.receita);

        return (
          <div className="text-right">
            <div className="text-brand-primary font-medium">
              <SensitiveValue mask="R$ ••••••">{formatCurrency(info.getValue())}</SensitiveValue>
            </div>
            {delta !== null && (
              <div className={`text-xs ${delta > 0 ? 'text-success' : delta < 0 ? 'text-error' : 'text-brand-gray'}`}>
                {delta > 0 ? '▲' : delta < 0 ? '▼' : ''}{delta > 0 ? '+' : ''}{delta.toFixed(1)}%
              </div>
            )}
          </div>
        );
      },
    }),
    columnHelper.accessor('custos', {
      header: () => <div className="text-right">Custos</div>,
      cell: (info) => (
        <div className="text-right text-error">
          <SensitiveValue mask="R$ ••••••">{formatCurrency(info.getValue())}</SensitiveValue>
        </div>
      ),
    }),
    columnHelper.accessor('margem', {
      header: () => <div className="text-right">Margem</div>,
      cell: (info) => {
        const value = info.getValue();
        return (
          <div className={`text-right font-semibold ${value > 0 ? 'text-success' : value < 0 ? 'text-error' : 'text-brand-gray'}`}>
            <SensitiveValue mask="R$ ••••••">{formatCurrency(value)}</SensitiveValue>
          </div>
        );
      },
    }),
    columnHelper.accessor('margemPercent', {
      header: () => <div className="text-right">Margem %</div>,
      cell: (info) => {
        const value = info.getValue();
        const styles = getMarginCellStyles(value);
        return (
          <div
            className={`${styles.backgroundColor} ${styles.textColor} text-right py-1 px-2 font-medium`}
            style={{ borderRadius: styles.borderRadius }}
          >
            {formatPercent(value)}
          </div>
        );
      },
    }),
    columnHelper.accessor('percentOfTotal', {
      header: () => <div className="text-right">% Total</div>,
      cell: (info) => {
        const value = info.getValue();
        const bu = info.row.original.businessUnit;
        return (
          <div className="flex items-center gap-2 justify-end">
            <div className="w-[60px] h-2 bg-brand-gray/20 rounded overflow-hidden">
              <div
                className="h-full rounded"
                style={{
                  width: `${Math.min(value, 100)}%`,
                  backgroundColor: BU_COLORS[bu as keyof typeof BU_COLORS] || '#D2FA52',
                }}
              />
            </div>
            <span className="text-sm text-brand-gray-light min-w-[40px]">
              {formatPercent(value)}
            </span>
          </div>
        );
      },
    }),
  ], [columnHelper, handleDrillDown, comparisonAggregations]);

  const table = useReactTable({
    data: buAggregations,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  // Loading state
  if (isLoading) {
    return <LoadingSkeleton />;
  }

  // Render
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-white mb-2 bg-gradient-to-r from-white to-brand-gray-light bg-clip-text text-transparent">
          Matriz de Business Units
        </h1>
        <p className="text-[#838998] font-body text-lg">
          Dashboard de rentabilidade por BU - Digital (3.01.0005, 3.01.0022, 3.02.0002) vs Promo (outros)
        </p>
      </div>

      {/* Controls Panel */}
      <DREControlsPanel
        startDate={startDate}
        endDate={endDate}
        onPeriodChange={(dates) => {
          setFilters({ startDate: dates.startDate, endDate: dates.endDate });
        }}
        onStartDateChange={(date) => setFilter('startDate', date)}
        onEndDateChange={(date) => setFilter('endDate', date)}
        onRefresh={refetch}
        isLoading={isLoading}
        comparisonMode={comparisonMode}
        onComparisonModeChange={(mode) => setFilter('comparisonMode', mode)}
        comparisonRange={comparisonRange}
        granularity={granularity}
        onGranularityChange={(gran) => setFilter('granularity', gran)}
      />

      {/* Error Display */}
      {error && (
        <div className="p-4 bg-brand-red/20 text-brand-red rounded-lg border border-brand-red/30">
          Erro: {error.message}
        </div>
      )}

      {buAggregations.length > 0 ? (
        <>
          {/* KPI Cards */}
          <div className="flex gap-3 flex-wrap">
            {KPI_DEFINITIONS.map((kpi) => {
              const value = kpi.getValue(totals);
              const compValue = comparisonTotals ? kpi.getValue(comparisonTotals) : undefined;
              const change = calcDelta(value, compValue);

              let subtitle: string | undefined;
              if (kpi.getSubtitle) {
                subtitle = kpi.getSubtitle(value, totals);
              }

              const formattedValue = kpi.format === 'number'
                ? value.toLocaleString('pt-BR')
                : formatCompactCurrency(value);

              const animationFormat = kpi.format === 'number' ? 'number' : 'compact-currency';

              return (
                <KPICard
                  key={kpi.key}
                  label={kpi.label}
                  value={formattedValue}
                  change={change}
                  subtitle={subtitle}
                  icon={kpi.icon}
                  numericValue={value}
                  animationFormat={animationFormat}
                />
              );
            })}
          </div>

          {/* Chart Section */}
          <div className="bg-brand-light/10 p-6 rounded-lg">
            <div className="flex justify-between items-center mb-3">
              <h3 className="m-0 text-base text-white font-semibold">
                Evolucao por BU
              </h3>
              <div className="flex gap-1">
                {(['receita', 'custos', 'margem'] as const).map((metric) => (
                  <button
                    key={metric}
                    onClick={() => setSelectedChartMetric(metric)}
                    className={`px-3 py-1.5 text-xs rounded cursor-pointer capitalize transition-all ${
                      selectedChartMetric === metric
                        ? 'border border-brand-primary bg-brand-primary/10 text-brand-primary font-semibold'
                        : 'border border-brand-gray/20 bg-brand-dark-light text-brand-gray font-normal'
                    }`}
                  >
                    {metric}
                  </button>
                ))}
              </div>
            </div>
            <BUChart
              data={chartData}
              selectedMetric={selectedChartMetric}
              height={280}
            />
          </div>

          {/* BU Table */}
          <div>
            <div className="flex justify-between items-center mb-3">
              <h3 className="m-0 text-base text-white font-semibold">
                Business Units por Rentabilidade
              </h3>
              <div className="flex gap-2">
                <button
                  onClick={handleExportCSV}
                  className="btn-primary"
                >
                  Exportar CSV
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="table-financial w-full border-collapse text-sm">
                <thead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <th
                          key={header.id}
                          onClick={header.column.getToggleSortingHandler()}
                          className={`p-3 border-b-2 border-brand-primary bg-brand-dark-light text-brand-gray whitespace-nowrap ${
                            header.id === 'businessUnit' ? 'text-left' : 'text-right'
                          } ${header.column.getCanSort() ? 'cursor-pointer' : 'cursor-default'}`}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {{
                            asc: ' ↑',
                            desc: ' ↓',
                          }[header.column.getIsSorted() as string] ?? null}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.map((row) => (
                    <tr
                      key={row.id}
                      className={row.index % 2 === 0 ? 'bg-brand-dark' : 'bg-brand-dark-light'}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          className="p-3 border-b border-brand-dark-light"
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
                {/* Totals Row */}
                <tfoot>
                  <tr className="bg-brand-primary/10 font-bold">
                    <td className="p-3 border-t-2 border-brand-primary text-white">
                      TOTAL
                    </td>
                    <td className="p-3 border-t-2 border-brand-primary text-right text-brand-gray-light">
                      {totals.notas}
                    </td>
                    <td className="p-3 border-t-2 border-brand-primary text-right text-brand-gray-light">
                      {totals.pits}
                    </td>
                    <td className="p-3 border-t-2 border-brand-primary text-right text-brand-gray-light">
                      {totals.produtos}
                    </td>
                    <td className="p-3 border-t-2 border-brand-primary text-right text-brand-primary">
                      <SensitiveValue mask="R$ ••••••">{formatCurrency(totals.receita)}</SensitiveValue>
                    </td>
                    <td className="p-3 border-t-2 border-brand-primary text-right text-error">
                      <SensitiveValue mask="R$ ••••••">{formatCurrency(totals.custos)}</SensitiveValue>
                    </td>
                    <td className={`p-3 border-t-2 border-brand-primary text-right ${totals.margem > 0 ? 'text-success' : 'text-error'}`}>
                      <SensitiveValue mask="R$ ••••••">{formatCurrency(totals.margem)}</SensitiveValue>
                    </td>
                    <td className="p-3 border-t-2 border-brand-primary text-right text-white">
                      {totals.receita > 0 ? formatPercent((totals.margem / totals.receita) * 100) : '-'}
                    </td>
                    <td className="p-3 border-t-2 border-brand-primary text-right text-white">
                      100%
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </>
      ) : (
        <p className="text-center text-brand-gray mt-8">
          Nenhum dado encontrado para o periodo selecionado.
        </p>
      )}

      {/* Mobile FAB and Action Sheet */}
      <FloatingActionButton
        onClick={() => setIsActionSheetOpen(true)}
        ariaLabel="Abrir menu de acoes"
      />
      <BottomActionSheet
        isOpen={isActionSheetOpen}
        onClose={() => setIsActionSheetOpen(false)}
        title="Acoes"
        actions={mobileActions}
      />
    </div>
  );
}

export default BUMatrixDashboard;
